import {
  type ReactNode,
  type ReactElement,
  Children,
  cloneElement,
  isValidElement,
} from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import clsx from 'clsx';
import { splitTextWithExtensions } from '@/lib/markdown-extensions';
import { ProvenanceChip } from '@/components/builder/ProvenanceChip';
import { DeepLink } from '@/components/builder/DeepLink';
import type { ProvenanceRecord, ModelSchema } from '@arcanum-hq/shared-types';

/**
 * ArcanumMarkdown — shared markdown renderer (ADR 0002, WS1 foundation).
 *
 * Renders markdown via react-markdown@10 + remark-gfm@4, then applies a
 * post-parse text-node walk that replaces `[arcanum:input:...]` /
 * `[arcanum:goto:...]` tokens with `<ProvenanceChip>` / `<DeepLink>`.
 *
 * ADR 0002 composition rationale:
 *   - Pre-split-then-render breaks markdown spanning chip boundaries (a list
 *     item containing a chip breaks).
 *   - Post-parse walk preserves BOTH cross-segment markdown AND chips in one
 *     renderer.
 *
 * Streaming-tolerant: partial markdown renders best-effort; the chip walk
 * runs on whatever text nodes exist at the time.
 *
 * No raw HTML rendered (react-markdown default — `allowedElements` not
 * relaxed, so `<script>`/`<iframe>` etc. stay inert).
 *
 * Safe links: all `<a>` elements get `rel="noopener noreferrer"` and
 * `javascript:` hrefs are blocked.
 */

export interface ArcanumMarkdownProps {
  /**
   * Raw markdown string to render. Malformed markdown degrades to plain text
   * (react-markdown's default behavior).
   */
  content: string;
  /**
   * Density variant:
   *   - `'default'` — `text-sm` (internal Builder chat)
   *   - `'compact'` — `text-xs` (public deal chat)
   */
  density?: 'compact' | 'default';
  /**
   * Provenance map for `<ProvenanceChip>` hydration. `null` renders chips
   * with agent-variant + empty popover (defensive).
   */
  inputProvenance?: Record<string, ProvenanceRecord> | null;
  /**
   * Active model schema for `<DeepLink>` validation. `null` renders links
   * as invalid chips.
   */
  modelSchema?: ModelSchema | null;
}

/** Safe link href — blocks `javascript:` protocol. */
function isSafeHref(href: string | undefined): boolean {
  if (!href) return false;
  const lower = href.toLowerCase().trim();
  return !lower.startsWith('javascript:');
}

/**
 * Walk a React node tree, replacing string text content that contains
 * `[arcanum:...]` tokens with the corresponding chip/link components.
 *
 * The walk is recursive — it handles arbitrarily nested element trees produced
 * by react-markdown (e.g. `<strong>`, `<code>`, `<td>`, `<li>`, ...).
 *
 * Returns a new node (or array of nodes) with all text replaced; non-text
 * nodes are cloned with their children recursively walked.
 */
function walkAndInjectChips(
  node: ReactNode,
  inputProvenance: Record<string, ProvenanceRecord> | null,
  modelSchema: ModelSchema | null,
  keyPrefix: string
): ReactNode {
  // Null / boolean / undefined — pass through
  if (node === null || node === undefined || typeof node === 'boolean') {
    return node;
  }

  // Numbers — pass through
  if (typeof node === 'number') {
    return node;
  }

  // String — this is the leaf where chip injection happens
  if (typeof node === 'string') {
    const segments = splitTextWithExtensions(node);
    if (segments.length === 0) return null;
    // Fast path: no extensions found
    if (segments.length === 1 && typeof segments[0] === 'string') {
      return node;
    }
    // Slow path: inject chips
    return segments.map((seg, i) => {
      if (typeof seg === 'string') {
        return <span key={`${keyPrefix}-s-${i}`}>{seg}</span>;
      }
      if (seg.kind === 'provenance') {
        return (
          <ProvenanceChip
            key={`${keyPrefix}-prov-${i}`}
            namedRange={seg.namedRange}
            value={seg.value}
            inputProvenance={inputProvenance ?? null}
          />
        );
      }
      // seg.kind === 'deep-link'
      return (
        <DeepLink
          key={`${keyPrefix}-dl-${i}`}
          parts={seg.parts}
          modelSchema={modelSchema ?? null}
        />
      );
    });
  }

  // Array — walk each element
  if (Array.isArray(node)) {
    return node.map((child, i) =>
      walkAndInjectChips(
        child,
        inputProvenance,
        modelSchema,
        `${keyPrefix}-a-${i}`
      )
    );
  }

  // React element — recursively walk children
  if (isValidElement(node)) {
    const el = node as ReactElement<{ children?: ReactNode }>;
    const { children } = el.props;
    if (children === undefined || children === null) return node;

    const walkedChildren = Children.map(children, (child, i) =>
      walkAndInjectChips(
        child,
        inputProvenance,
        modelSchema,
        `${keyPrefix}-c-${i}`
      )
    );
    return cloneElement(el, {}, ...(walkedChildren ?? []));
  }

  // Everything else (fragments etc.) — pass through
  return node;
}

/**
 * Build the react-markdown `components` override map for the Intelligence
 * Terminal token set.
 *
 * The override map is expressed as a factory so each `<ArcanumMarkdown>`
 * instance gets its own closure over `density`, `inputProvenance`,
 * `modelSchema` — no module-level state.
 *
 * Note: react-markdown v10 passes `children` as ReactNode; the components
 * receive the parsed + rendered subtree, which is then walked for chip tokens.
 */
function buildComponents(
  density: 'compact' | 'default',
  inputProvenance: Record<string, ProvenanceRecord> | null,
  modelSchema: ModelSchema | null
) {
  const textSize = density === 'compact' ? 'text-xs' : 'text-sm';

  // Helper: chip-inject children from the component props
  function inject(children: ReactNode, keyPrefix: string): ReactNode {
    return walkAndInjectChips(
      children,
      inputProvenance,
      modelSchema,
      keyPrefix
    );
  }

  return {
    // ----- Headings -----
    h1: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <div
        className={clsx(
          'font-semibold text-text-bright mt-3 mb-1 font-body',
          textSize === 'text-xs' ? 'text-sm' : 'text-lg'
        )}
        {...(rest as Record<string, unknown>)}
      >
        {inject(children, 'h1')}
      </div>
    ),
    h2: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <div
        className={clsx(
          'font-semibold text-text-bright mt-3 mb-1 font-body',
          textSize === 'text-xs' ? 'text-xs' : 'text-base'
        )}
        {...(rest as Record<string, unknown>)}
      >
        {inject(children, 'h2')}
      </div>
    ),
    h3: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <div
        className={clsx(
          'font-semibold text-text-bright mt-2 mb-0.5 font-body',
          textSize
        )}
        {...(rest as Record<string, unknown>)}
      >
        {inject(children, 'h3')}
      </div>
    ),
    h4: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <div
        className={clsx(
          'font-medium text-text mt-2 mb-0.5 font-body',
          textSize
        )}
        {...(rest as Record<string, unknown>)}
      >
        {inject(children, 'h4')}
      </div>
    ),

    // ----- Paragraph -----
    p: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <div
        className={clsx('mb-1 leading-relaxed', textSize)}
        {...(rest as Record<string, unknown>)}
      >
        {inject(children, 'p')}
      </div>
    ),

    // ----- Strong / Bold -----
    strong: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <strong
        className="text-accent font-semibold"
        {...(rest as Record<string, unknown>)}
      >
        {inject(children, 'strong')}
      </strong>
    ),

    // ----- Emphasis / Italic -----
    em: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <em
        className="italic text-text-bright"
        {...(rest as Record<string, unknown>)}
      >
        {inject(children, 'em')}
      </em>
    ),

    // ----- Lists -----
    ul: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <ul
        className="mb-1 ml-3 list-none"
        {...(rest as Record<string, unknown>)}
      >
        {children}
      </ul>
    ),
    ol: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <ol
        className="mb-1 ml-3 list-decimal list-inside"
        {...(rest as Record<string, unknown>)}
      >
        {children}
      </ol>
    ),
    li: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <li
        className={clsx(
          "before:content-['•'] before:mr-2 before:text-text-dim",
          textSize
        )}
        {...(rest as Record<string, unknown>)}
      >
        {inject(children, 'li')}
      </li>
    ),

    // ----- Inline code -----
    code: ({
      children,
      className,
      ...rest
    }: {
      children?: ReactNode;
      className?: string;
      [k: string]: unknown;
    }) => {
      // react-markdown v10: fenced code blocks pass className="language-*";
      // inline code has no className. This component handles BOTH.
      const isBlock = Boolean(className);
      if (isBlock) {
        return (
          <pre className="my-2 p-2 bg-panel-bg border border-panel-border rounded-[3px] overflow-x-auto">
            <code
              className={clsx(
                'font-mono tabular-nums text-text-bright',
                density === 'compact' ? 'text-[10px]' : 'text-xs'
              )}
            >
              {children}
            </code>
          </pre>
        );
      }
      return (
        <code
          className={clsx(
            'font-mono tabular-nums text-text-bright bg-panel-bg border border-panel-border rounded-[2px] px-1',
            density === 'compact' ? 'text-[10px]' : 'text-xs'
          )}
          {...(rest as Record<string, unknown>)}
        >
          {children}
        </code>
      );
    },

    // ----- Horizontal rule -----
    hr: () => <hr className="border-0 border-t border-panel-border my-3" />,

    // ----- Links — safe only, rel=noopener noreferrer -----
    a: ({
      href,
      children,
      ...rest
    }: {
      href?: string;
      children?: ReactNode;
      [k: string]: unknown;
    }) => {
      if (!isSafeHref(href)) {
        // Strip unsafe hrefs — render as plain span
        return <span>{inject(children, 'a-unsafe')}</span>;
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent underline underline-offset-2 hover:text-text-bright transition-colors"
          {...(rest as Record<string, unknown>)}
        >
          {inject(children, 'a')}
        </a>
      );
    },

    // ----- GFM table -----
    table: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <div className="overflow-x-auto my-2">
        <table
          className="w-full border-collapse border border-panel-border"
          {...(rest as Record<string, unknown>)}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <thead className="bg-panel-bg" {...(rest as Record<string, unknown>)}>
        {children}
      </thead>
    ),
    tbody: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => <tbody {...(rest as Record<string, unknown>)}>{children}</tbody>,
    tr: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <tr
        className="border-b border-panel-border"
        {...(rest as Record<string, unknown>)}
      >
        {children}
      </tr>
    ),
    th: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <th
        className={clsx(
          'font-mono tabular-nums font-semibold text-text-bright text-left px-2 py-1 border-r border-panel-border',
          density === 'compact' ? 'text-[10px]' : 'text-xs'
        )}
        {...(rest as Record<string, unknown>)}
      >
        {inject(children, 'th')}
      </th>
    ),
    td: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <td
        className={clsx(
          'px-2 py-1 border-r border-panel-border text-text',
          textSize
        )}
        {...(rest as Record<string, unknown>)}
      >
        {inject(children, 'td')}
      </td>
    ),

    // ----- Blockquote -----
    blockquote: ({
      children,
      ...rest
    }: {
      children?: ReactNode;
      [k: string]: unknown;
    }) => (
      <blockquote
        className="border-l-2 border-panel-border pl-3 my-1 text-text-muted italic"
        {...(rest as Record<string, unknown>)}
      >
        {children}
      </blockquote>
    ),
  };
}

/**
 * `<ArcanumMarkdown>` — shared markdown renderer for chat surfaces.
 *
 * Usage:
 * ```tsx
 * // Builder chat (internal, default density)
 * <ArcanumMarkdown content={message.content} inputProvenance={provenance} modelSchema={schema} />
 *
 * // Public deal chat (compact density)
 * <ArcanumMarkdown content={message.content} density="compact" />
 * ```
 */
export function ArcanumMarkdown({
  content,
  density = 'default',
  inputProvenance = null,
  modelSchema = null,
}: ArcanumMarkdownProps) {
  const components = buildComponents(density, inputProvenance, modelSchema);
  const textSize = density === 'compact' ? 'text-xs' : 'text-sm';

  return (
    <div
      className={clsx('text-text font-body leading-relaxed', textSize)}
      data-testid="arcanum-markdown"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={
          // Double cast required: our per-component prop shapes use a
          // `[k: string]: unknown` index signature for spread safety, which
          // TypeScript can't structurally reconcile with react-markdown's
          // precise intrinsic-element prop types. The runtime shape is
          // correct — each component receives exactly the HTML element props
          // react-markdown passes; the cast is a TS-only concession.
          components as unknown as Components
        }
        // No allowedElements / disallowedElements — react-markdown's default
        // already strips dangerous HTML. raw HTML passthrough (rehype-raw) is
        // intentionally NOT added so `<script>` stays inert.
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
